import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { categoryName } from "../data/site";

export default function ProductCard({ product: p, index = 0, className = "" }) {
  const { addItem } = useCart();
  const [size, setSize] = useState(p.sizes?.[1] || p.sizes?.[0] || "4Y");
  const soldOut = p.stock != null && p.stock <= 0;
  const lowStock = !soldOut && p.stock != null && p.stock <= 3;
  const onSale = p.compare_at != null && p.compare_at > p.price;

  const add = () => {
    if (soldOut) return;
    addItem({
      productId: p.id,
      name: p.name,
      price: p.price,
      image: p.image,
      size,
    });
    toast.success(`${p.name} added to your bag`, {
      description: `Size ${size} · £${p.price.toFixed(2)}`,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${p.id}`}
      className={`group flex flex-col ${className}`}
    >
      <Link
        to={`/product/${p.id}`}
        data-testid={`product-link-${p.id}`}
        aria-label={`View ${p.name}`}
        className="relative block overflow-hidden rounded-[28px] bg-blush-50"
      >
        <div className="aspect-[4/5] w-full overflow-hidden">
          <img
            src={p.image}
            alt={p.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
          />
          {p.images?.[0] && (
            <img
              src={p.images[0]}
              alt={`${p.name} — another style`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            />
          )}
        </div>
        {p.badge && (
          <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink backdrop-blur">
            {p.badge}
          </span>
        )}
        {onSale && (
          <span className="absolute right-3 top-3 rounded-full bg-blush-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
            Sale
          </span>
        )}
        {soldOut && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px]">
            <span className="rounded-full bg-ink px-4 py-2 font-display text-xs font-semibold uppercase tracking-[0.2em] text-white">
              Sold out
            </span>
          </div>
        )}
        {!soldOut && (
          <div className="absolute inset-x-3 bottom-3 transition-all duration-300 md:translate-y-3 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100">
            <button
              data-testid={`add-to-bag-${p.id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                add();
              }}
              className="w-full rounded-full bg-ink py-3 font-display text-sm font-semibold text-white shadow-lg transition-colors duration-300 hover:bg-ink/90"
            >
              Add to Bag
            </button>
          </div>
        )}
      </Link>

      <div className="mt-4 flex flex-1 flex-col px-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blush-700">
          {categoryName(p.category_id)}
        </p>
        <h3 className="mt-1 font-display text-lg font-semibold leading-snug text-ink">
          <Link to={`/product/${p.id}`} data-testid={`product-title-link-${p.id}`} className="transition-colors duration-200 hover:text-blush-700">
            {p.name}
          </Link>
        </h3>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(p.sizes || []).map((s) => (
            <button
              key={s}
              data-testid={`size-chip-${p.id}-${s}`}
              onClick={() => setSize(s)}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors duration-200 ${
                size === s
                  ? "bg-ink text-white"
                  : "bg-lav-50 text-muted hover:bg-lav-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="mt-2.5 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg font-semibold text-ink">
              £{p.price.toFixed(2)}
            </span>
            {onSale && (
              <span className="text-sm text-muted line-through">
                £{p.compare_at.toFixed(2)}
              </span>
            )}
          </div>
          {lowStock ? (
            <span className="text-xs font-semibold text-blush-700">Only {p.stock} left</span>
          ) : (
            <span className="text-xs text-muted">30-day happy returns</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
